import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, DollarSign, FileText, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { adminService } from "../services/adminService";
import SearchBar      from "../../../components/ui/SearchBar";
import FilterDropdown from "../../../components/ui/FilterDropdown";
import AvatarCell     from "../../../components/ui/AvatarCell";
import PlanBadge      from "../../../components/ui/PlanBadge";
import StatusBadge    from "../../../components/ui/StatusBadge";
import KpiCard        from "../../../components/ui/KpiCard";
import DataTable      from "../../../components/ui/DataTable";
import { PLAN_TIERS } from "../../../constants/plans";

const PAGE_SIZE = 10;

// Map API payment object → table row shape
const mapTx = (p) => {
  const method = p.payment_method?.display ?? "—";

  const date = p.date
    ? new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  const rawStatus = (p.status ?? "").toLowerCase();
  const status =
    rawStatus === "succeeded" || rawStatus === "paid"       ? "paid"   :
    rawStatus === "failed"    || rawStatus === "incomplete" ? "failed" :
    rawStatus || "paid";

  return {
    id:         p.invoice_id ?? "—",
    name:       p.customer?.name    ?? "—",
    company:    p.customer?.company ?? p.customer?.email ?? "",
    plan:       p.plan              ?? "—",
    amount:     p.amount            ?? 0,
    date,
    method,
    status,
    receiptUrl: p.receipt_url       ?? null,
  };
};

const PLAN_OPTIONS = [
  { label: "All Plans", value: "all" },
  ...PLAN_TIERS.map((t) => ({ label: t, value: t })),
];

const STATUS_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Paid",       value: "paid" },
  { label: "Failed",     value: "failed" },
];

const COLUMNS = [
  {
    header: "Invoice ID",
    render: (tx) => (
      <span className="bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100 whitespace-nowrap">
        {tx.id}
      </span>
    ),
  },
  {
    header: "Customer",
    render: (tx) => <AvatarCell name={tx.name} sub={tx.company} />,
  },
  {
    header: "Plan",
    render: (tx) => <PlanBadge plan={tx.plan} />,
  },
  {
    header: "Amount",
    align: "right",
    render: (tx) => (
      <span className={`text-sm font-bold ${tx.status === "failed" ? "text-danger" : "text-brand-text"}`}>
        ${tx.amount.toFixed(2)}
      </span>
    ),
  },
  {
    header: "Date",
    render: (tx) => (
      <span className="text-sm text-brand-muted whitespace-nowrap">{tx.date}</span>
    ),
  },
  {
    header: "Payment Method",
    render: (tx) => (
      <div className="flex items-center gap-2 text-sm text-brand-text whitespace-nowrap">
        <CreditCard className="w-4 h-4 text-brand-muted flex-shrink-0" />
        {tx.method}
      </div>
    ),
  },
  {
    header: "Status",
    render: (tx) => <StatusBadge status={tx.status} />,
  },
  {
    header: "Action",
    render: (tx) => tx.receiptUrl ? (
      <a href={tx.receiptUrl} target="_blank" rel="noreferrer" className="text-brand-muted hover:text-primary transition-colors" title="View receipt">
        <ExternalLink className="w-4 h-4" />
      </a>
    ) : (
      <span className="text-brand-light" title="No receipt">
        <FileText className="w-4 h-4" />
      </span>
    ),
  },
];

const AdminPaymentHistoryPage = () => {
  const [rows,         setRows]         = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTx,      setTotalTx]      = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [inputValue,   setInputValue]   = useState("");
  const [search,       setSearch]       = useState("");
  const [planFilter,   setPlanFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Cursor-based pagination — stack of previous cursors for Back
  const [cursor,       setCursor]       = useState(null);  // current page cursor
  const [cursorStack,  setCursorStack]  = useState([]);    // previous cursors
  const [hasMore,      setHasMore]      = useState(false);
  const [nextCursor,   setNextCursor]   = useState(null);

  const fetchData = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const res = await adminService.getAdminPaymentHistory({
        limit:    PAGE_SIZE,
        cursor:   cursor || undefined,
        search:   search || undefined,
        planTier: planFilter  !== "all" ? planFilter  : undefined,
        status:   statusFilter !== "all" ? statusFilter : undefined,
      });
      if (cancelled) return;
      const items      = res?.data?.transactions ?? [];
      const stats      = res?.data?.stats        ?? {};
      const pagination = res?.data?.pagination   ?? {};
      setRows(Array.isArray(items) ? items.map(mapTx) : []);
      setTotalRevenue(stats.total_revenue     ?? 0);
      setTotalTx(stats.total_transactions     ?? items.length);
      setHasMore(pagination.has_more          ?? false);
      setNextCursor(pagination.next_page_cursor ?? null);
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [cursor, search, planFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetPagination = () => { setCursor(null); setCursorStack([]); };

  const handlePlanChange   = (val) => { setPlanFilter(val);   resetPagination(); };
  const handleStatusChange = (val) => { setStatusFilter(val); resetPagination(); };
  const handleSearchCommit = (val) => { setSearch(val);       resetPagination(); };
  const handleSearchClear  = ()    => { setSearch("");        resetPagination(); };
  const handleSearchChange = (val) => {
    setInputValue(val);
    if (val === "") { setSearch(""); resetPagination(); }
  };

  const handleNext = () => {
    if (!hasMore || !nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    if (!cursorStack.length) return;
    const stack = [...cursorStack];
    const prev  = stack.pop();
    setCursorStack(stack);
    setCursor(prev);
  };

  const pageNum = cursorStack.length + 1;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-brand-text">Payment History</h1>
        <p className="text-sm text-brand-muted mt-0.5">All platform transactions</p>
      </div>

      {/* KPI row */}
      <div className="flex gap-4 flex-wrap">
        <KpiCard
          icon={DollarSign}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          value={`$${Number(totalRevenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          label="Total Revenue Collected"
        />
        <KpiCard
          icon={CreditCard}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          value={totalTx}
          label="Total Transactions"
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-danger bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <SearchBar
          value={inputValue}
          onChange={handleSearchChange}
          onCommit={handleSearchCommit}
          onClear={handleSearchClear}
          placeholder="Search by Customer Name"
          className="min-w-[260px]"
        />
        <div className="flex-1" />
        <FilterDropdown value={planFilter}   onChange={handlePlanChange}   options={PLAN_OPTIONS}   placeholder="All Plans" />
        <FilterDropdown value={statusFilter} onChange={handleStatusChange} options={STATUS_OPTIONS} placeholder="All Status" />
      </div>

      {/* Table */}
      <DataTable
        columns={COLUMNS}
        rows={rows}
        loading={loading}
        emptyText="No transactions found."
        page={pageNum}
        totalPages={hasMore ? pageNum + 1 : pageNum}
        onPageChange={(p) => { if (p > pageNum) handleNext(); else if (p < pageNum) handlePrev(); }}
        totalCount={totalTx}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
};

export default AdminPaymentHistoryPage;
